import type { MorphemZerlegungExerciseConfig, MorphemZerlegungRunSettings } from "@calega/content";
import type { QualitativeEvidenceRef } from "../analysis-contract";
import { accuracy, qualitativeEvidenceRefs } from "../analysis-utils";
import { randomGenerator } from "../run-random";
import { normalizedItemText, pickRunItems, type RunSelection } from "../run-selection";

export interface MorphemSegments {
  prefix: string;
  stem: string;
  suffix: string;
}

export interface MorphemZerlegungRunItem {
  id: string;
  sourceId: string;
  word: string;
  lemma: string;
  prefix: string;
  stem: string;
  suffix: string;
  syllableSegments?: [string, string, string];
  difficulty: "1" | "2" | "3";
  expectedCuts: [number, number];
}

export interface MorphemZerlegungRun {
  seed: string;
  generatorVersion: 2;
  exerciseType: "morphem-zerlegung";
  settings: MorphemZerlegungRunSettings;
  items: MorphemZerlegungRunItem[];
}

export interface MorphemZerlegungAnswer {
  itemKey: string;
  cuts: [number, number];
}

export type MorphemZerlegungErrorSubtype =
  | "confirmed_selection"
  | "syllable_trap"
  | "prefix_stem_blend"
  | "suffix_stem_blend"
  | "prefix_slot_omitted"
  | "suffix_slot_omitted"
  | "invented_prefix"
  | "invented_suffix"
  | "boundary_error";

export function morphemSegmentsFromCuts(word: string, cuts: [number, number]): MorphemSegments | null {
  const characters = Array.from(word);
  const [first, second] = cuts;
  if (!Number.isInteger(first) || !Number.isInteger(second) || first < 0 || first >= second || second > characters.length) {
    return null;
  }
  return {
    prefix: characters.slice(0, first).join(""),
    stem: characters.slice(first, second).join(""),
    suffix: characters.slice(second).join(""),
  };
}

export function classifyMorphemZerlegungSegments(
  expected: MorphemSegments,
  selected: MorphemSegments,
  syllableSegments?: [string, string, string],
): MorphemZerlegungErrorSubtype {
  if (selected.prefix === expected.prefix && selected.stem === expected.stem && selected.suffix === expected.suffix) {
    return "confirmed_selection";
  }
  if (
    syllableSegments
    && selected.prefix === syllableSegments[0]
    && selected.stem === syllableSegments[1]
    && selected.suffix === syllableSegments[2]
  ) {
    return "syllable_trap";
  }
  if (
    expected.prefix !== ""
    && selected.prefix === ""
    && selected.stem === expected.prefix + expected.stem
    && selected.suffix === expected.suffix
  ) {
    return "prefix_stem_blend";
  }
  if (
    expected.suffix !== ""
    && selected.suffix === ""
    && selected.prefix === expected.prefix
    && selected.stem === expected.stem + expected.suffix
  ) {
    return "suffix_stem_blend";
  }
  if (expected.prefix !== "" && selected.prefix === "") return "prefix_slot_omitted";
  if (expected.prefix === "" && selected.prefix !== "") return "invented_prefix";
  if (expected.suffix !== "" && selected.suffix === "") return "suffix_slot_omitted";
  if (expected.suffix === "" && selected.suffix !== "") return "invented_suffix";
  return "boundary_error";
}

export function classifyMorphemZerlegungAnswer(item: MorphemZerlegungRunItem, cuts: [number, number]) {
  const selectedSegments = morphemSegmentsFromCuts(item.word, cuts);
  if (!selectedSegments) return null;
  const expectedSegments = { prefix: item.prefix, stem: item.stem, suffix: item.suffix };
  const errorSubtype = classifyMorphemZerlegungSegments(expectedSegments, selectedSegments, item.syllableSegments);
  return {
    correct: errorSubtype === "confirmed_selection",
    prefixCorrect: selectedSegments.prefix === item.prefix,
    stemCorrect: selectedSegments.stem === item.stem,
    suffixCorrect: selectedSegments.suffix === item.suffix,
    selectedSegments,
    expectedSegments,
    errorSubtype,
  };
}

export function generateMorphemZerlegungRun(
  config: MorphemZerlegungExerciseConfig,
  settings: MorphemZerlegungRunSettings,
  seed: string,
  selection?: RunSelection,
): MorphemZerlegungRun {
  const random = randomGenerator(seed);
  const total = selection?.count ?? (settings.mode === "time" ? Math.max(120, settings.durationSeconds * 2) : settings.count);
  const selected = pickRunItems(config.items, total, random, (item) => normalizedItemText(item.word), selection?.unique);
  return {
    seed,
    generatorVersion: 2,
    exerciseType: "morphem-zerlegung",
    settings,
    items: selected.map((item, index) => ({
      ...item,
      id: `morphem-zerlegung-${index + 1}-${item.id}`,
      sourceId: item.id,
      expectedCuts: [
        Array.from(item.prefix).length,
        Array.from(item.prefix + item.stem).length,
      ],
    })),
  };
}

interface MorphemZerlegungAccuracy {
  total: number;
  correct: number;
  accuracy: number | null;
}

export interface MorphemZerlegungAnalysis {
  schemaVersion: "word-diagnostics-v1.1";
  exerciseType: "morphem-zerlegung";
  answered: number;
  total: number;
  correct: number;
  accuracy: number | null;
  segments: Record<"prefix" | "stem" | "suffix", MorphemZerlegungAccuracy>;
  contexts: Record<"prefixPresent" | "prefixAbsent" | "suffixPresent" | "suffixAbsent" | "syllableCandidate", MorphemZerlegungAccuracy>;
  subtypeCounts: Record<MorphemZerlegungErrorSubtype, number>;
  timeline: Array<{
    order: number;
    itemKey: string;
    word: string;
    lemma: string;
    selectedSegments: MorphemSegments;
    expectedSegments: MorphemSegments;
    errorSubtype: MorphemZerlegungErrorSubtype;
  }>;
  hypotheses: Array<{
    ruleId:
      | "morphem-zerlegung.syllable_trap"
      | "morphem-zerlegung.prefix_stem_blend"
      | "morphem-zerlegung.suffix_stem_blend"
      | "morphem-zerlegung.prefix_slot_instability"
      | "morphem-zerlegung.suffix_slot_instability";
    status: "triggered" | "not_triggered" | "insufficient_data";
    relevantCount: number;
    evidenceCount: number;
    evidenceRefs: QualitativeEvidenceRef[];
  }>;
}

export function analyzeMorphemZerlegungRun(
  run: MorphemZerlegungRun,
  answers: MorphemZerlegungAnswer[],
): MorphemZerlegungAnalysis {
  const latest = new Map(answers.map((answer) => [answer.itemKey, answer]));
  const rows = run.items.flatMap((item, index) => {
    const answer = latest.get(item.id);
    if (!answer) return [];
    const result = classifyMorphemZerlegungAnswer(item, answer.cuts);
    return result ? [{ item, order: index + 1, result }] : [];
  });
  const stat = (matching: typeof rows) => ({
    total: matching.length,
    correct: matching.filter(({ result }) => result.correct).length,
    accuracy: accuracy(matching.filter(({ result }) => result.correct).length, matching.length),
  });
  const segmentStat = (part: "prefix" | "stem" | "suffix"): MorphemZerlegungAccuracy => {
    const correct = rows.filter(({ result }) => result[`${part}Correct`]).length;
    return { total: rows.length, correct, accuracy: accuracy(correct, rows.length) };
  };
  const prefixPresent = rows.filter(({ item }) => item.prefix !== "");
  const prefixAbsent = rows.filter(({ item }) => item.prefix === "");
  const suffixPresent = rows.filter(({ item }) => item.suffix !== "");
  const suffixAbsent = rows.filter(({ item }) => item.suffix === "");
  const syllableCandidates = rows.filter(({ item }) => item.syllableSegments);
  const contextSegmentStat = (matching: typeof rows, part: "prefix" | "suffix"): MorphemZerlegungAccuracy => {
    const correct = matching.filter(({ result }) => result[`${part}Correct`]).length;
    return { total: matching.length, correct, accuracy: accuracy(correct, matching.length) };
  };
  const syllableCorrect = syllableCandidates.filter(({ result }) => result.errorSubtype !== "syllable_trap").length;
  const subtypes: MorphemZerlegungErrorSubtype[] = [
    "confirmed_selection",
    "syllable_trap",
    "prefix_stem_blend",
    "suffix_stem_blend",
    "prefix_slot_omitted",
    "suffix_slot_omitted",
    "invented_prefix",
    "invented_suffix",
    "boundary_error",
  ];
  const subtypeCounts = Object.fromEntries(subtypes.map((subtype) => [
    subtype,
    rows.filter(({ result }) => result.errorSubtype === subtype).length,
  ])) as Record<MorphemZerlegungErrorSubtype, number>;
  const patternStatus = (relevant: number, evidence: number, minimum: number) => relevant < minimum
    ? "insufficient_data" as const
    : evidence >= 2 || evidence / relevant >= 0.3
      ? "triggered" as const
      : "not_triggered" as const;
  const hypothesis = (
    ruleId: MorphemZerlegungAnalysis["hypotheses"][number]["ruleId"],
    relevantCount: number,
    evidenceRows: typeof rows,
    minimum: number,
  ) => ({
    ruleId,
    status: patternStatus(relevantCount, evidenceRows.length, minimum),
    relevantCount,
    evidenceCount: evidenceRows.length,
    evidenceRefs: qualitativeEvidenceRefs(evidenceRows.map(({ item }) => item.id)),
  });
  const syllableEvidence = syllableCandidates.filter(({ result }) => result.errorSubtype === "syllable_trap");
  const prefixBlendEvidence = prefixPresent.filter(({ result }) => result.errorSubtype === "prefix_stem_blend");
  const suffixBlendEvidence = suffixPresent.filter(({ result }) => result.errorSubtype === "suffix_stem_blend");
  const prefixSlotEvidence = rows.filter(({ result }) => result.errorSubtype === "prefix_slot_omitted" || result.errorSubtype === "invented_prefix");
  const suffixSlotEvidence = rows.filter(({ result }) => result.errorSubtype === "suffix_slot_omitted" || result.errorSubtype === "invented_suffix");
  const overall = stat(rows);
  return {
    schemaVersion: "word-diagnostics-v1.1",
    exerciseType: "morphem-zerlegung",
    answered: rows.length,
    total: run.items.length,
    correct: overall.correct,
    accuracy: overall.accuracy,
    segments: {
      prefix: segmentStat("prefix"),
      stem: segmentStat("stem"),
      suffix: segmentStat("suffix"),
    },
    contexts: {
      prefixPresent: contextSegmentStat(prefixPresent, "prefix"),
      prefixAbsent: contextSegmentStat(prefixAbsent, "prefix"),
      suffixPresent: contextSegmentStat(suffixPresent, "suffix"),
      suffixAbsent: contextSegmentStat(suffixAbsent, "suffix"),
      syllableCandidate: {
        total: syllableCandidates.length,
        correct: syllableCorrect,
        accuracy: accuracy(syllableCorrect, syllableCandidates.length),
      },
    },
    subtypeCounts,
    timeline: rows.map(({ item, order, result }) => ({
      order,
      itemKey: item.id,
      word: item.word,
      lemma: item.lemma,
      selectedSegments: result.selectedSegments,
      expectedSegments: result.expectedSegments,
      errorSubtype: result.errorSubtype,
    })),
    hypotheses: [
      hypothesis("morphem-zerlegung.syllable_trap", syllableCandidates.length, syllableEvidence, 3),
      hypothesis("morphem-zerlegung.prefix_stem_blend", prefixPresent.length, prefixBlendEvidence, 3),
      hypothesis("morphem-zerlegung.suffix_stem_blend", suffixPresent.length, suffixBlendEvidence, 3),
      hypothesis("morphem-zerlegung.prefix_slot_instability", rows.length, prefixSlotEvidence, 4),
      hypothesis("morphem-zerlegung.suffix_slot_instability", rows.length, suffixSlotEvidence, 4),
    ],
  };
}
